// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit, ViewChild } from '@angular/core';
import { HeaderService } from '../../header.service';
import { D3eService } from '../../../services/d3e-service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    getProtectorDisplayValue, getVolumeEncryptionTypeDisplay,
    IDeviceState,
    IEndpoint, IEndpointExportToken,
    IPBAInfo,
    IPendingList,
    ISubscription, IVolume, IVolumeProtection, VolumeEncryptionType,
    VolumeProtectorType,
} from '../../../types/subscription';
import { SelectedOrgMethod } from '../../../shared/selectedOrg';
import {catchError, debounceTime, map, bufferCount, reduce, switchMap, withLatestFrom, concatMap, mergeMap, toArray} from 'rxjs/operators';
import {EMPTY, forkJoin, from, Observable, of} from 'rxjs';
import { compareLicenseVersions, LicenseMethods } from '../../../shared/license.methods';
import { AlertDialogComponent, IAlertDialogData } from '../../dialogs/alert/alert.component';
import { AlertClipboardCopyComponent } from '../../dialogs/alert-clipboard-copy/alert-clipboard-copy.component';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '../../../services/user-service';
import {
    ColumnChooserComponent,
    IColumnChooserElement
} from '../../shared/components/column-chooser/column-chooser.component';
import { TranslateService } from '@ngx-translate/core';
import authHelper from 'src/app/auth/authHelper';
import {ALL_ORGS_ID, OrgType} from '../../../types/group';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IFdeLicenseInfo, IFdeUser, IForcePolicyResetRequest, IPbaLicenseInfo, PolicyCompliance } from '../../../types/d3ePolicy';
import { NotificationService } from '../../../services/notification-service';
import {
    ILockEndpointsDialogResult,
    LockEndpointsDialogComponent
} from './lock-endpoints-dialog/lock-endpoints-dialog.component';
import { IEndpointsResponse } from '../../../types/endpoints';
import { TransferLicenseDialogComponent } from './transfer-license-dialog/transfer-license-dialog.component';
import { ConfigurationService } from '../../../services/configuration-service';
import multiDownload from 'multi-download';
import {BitLockerCompliance} from '../../../types/bitlocker-compliance';
import {Store} from '@ngrx/store';
import {AppState} from '../../store/app.state';
import {IManageUser, UserRole} from '../../../types/user';
import {selectUser} from '../../store/user/user.selectors';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { IEndpointUserDialogOptions, EndpointUserDialogComponent, EndpointUserType } from '../../shared/components/endpoint-user-dialog/endpoint-user-dialog.component';
import {IPbaUser} from '../../../types/d3ePolicy';
import { EnrollCodesDialogComponent, IAddEnrollCodesResponse, IEnrollCodesDialogOptions, IEnrollCodesDialogResult } from '../../shared/components/enroll-codes-dialog/enroll-codes-dialog.component';
import { EnrollCodesAddedDialogComponent, IEnrollCodesAddedDialogOptions } from '../../shared/components/enroll-codes-dialog/enroll-codes-added-dialog.component';
import { ActionConfirmationDialogComponent, ConsoleAction, getActionDialogData, IActionConfirmationDialogData, IActionConfirmationDialogResult } from '../../shared/components/action-confirmation-dialog/action-confirmation-dialog.component';
import { IManageEndpointUserDialogData, ManageEndpointUserDialogComponent } from 'src/app/shared/components/endpoint-user-dialog/manage-endpoint-user-dialog.component';
// Enable the UTC plugin
dayjs.extend(utc);

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-endpoints',
    templateUrl: './endpoints.component.html',
    styleUrls: ['./endpoints.component.scss'],
    standalone: false
})
export class EndpointsComponent implements OnInit {
    user$: Observable<IManageUser> = this.store.select(selectUser())

    selectedOrgId: string;
    endpointsDataLoaded = false;

    totalResults = null;

    gettingEndpoints = false;

    queryPageStart = 0;
    queryPageSize = 50;

    columnsToDisplay: string[] = [];

    @ViewChild(ColumnChooserComponent) columnChooser: ColumnChooserComponent;

    columns: IColumnChooserElement[] = [];

    // query stuff
    searchValue: string = '';

    selectAllChecked = false;
    endpointsSelected = false;

    endpoints: { checked: boolean; endpointData: IEndpoint } [] = [];

    nameFilterForm = new UntypedFormControl();

    deviceSetsEnabled;
    deviceSets = [];
    policies = [];

    defaultGroupFilterId = 0;
    defaultPolicyFilterId = 0;
    selectedSetId = 0;
    selectedPolicyId = 0;

    showPolicies = true;
    showPbaInstallMenuItem: boolean;
    showPbaWipeMenuItem: boolean;
    showPbaDisableMenuItem: boolean;
    containsPba: boolean;
    containsFde: boolean;
    singlePbaSelected: boolean;
    singleFdeSelected: boolean;
    agentOnEndpoint: boolean;
    showAdmin: boolean;
    isDataDefense: boolean;

    public readonly BitLockerCompliance = BitLockerCompliance;

    // ENDPOINTS FIELDS //
    fAssignToGroupButton: string;
    fLockButton: string;
    fUnlockButton: string;
    fEndpoints: string;
    fEndpointName: string;
    fNameFilter: string;
    fD3EV: string;
    fManualLock: string;
    fActiveLock: string;
    fOS: string;
    fLastLoggedIn: string;
    fLastLoggedInTime: string;
    fGroup: string;
    fPolicy: string;
    fLastSeen: string;
    fNoData: string;
    fLockTitle: string;
    fLockContent: string;
    fUnlockTitle: string;
    fUnlockContent: string;
    fLocked: string;
    fUnlocked: string;
    fStandingGuard: string;
    fThreatDetected: string;
    fPendingLock: string;
    fPendingUnlock: string;
    fConnected: string;
    fNever: string;
    fYes: string;
    fNo: string;
    fAvail: string;
    fNotAvail: string;

    title: string;
    pinResetEnabled = false;
    resetableSelected = false;
    pinResetVersion: string = null;
    selectedBitlockerCompliance = null;

    sortAttrs = [{'sortAttr': 'deviceName', 'sortOrder': 'asc'}];
    orgType: OrgType;
    readonly OrgType = OrgType;
    targets = ['agent', 'inventory', 'status', 'protections', 'encryption'];

    selectedTarget: string = this.targets[0];
    targetColumnsIndex = 0;
    targetColumnsMetadata: IColumnChooserElement[][] = [];
    targetColumns: IColumnChooserElement[] = [];
    targetSaveId = 'endpoints=-' + this.selectedTarget;
    private queryColumnNames = {};

    constructor(
        private headerService: HeaderService,
        private d3eService: D3eService,
        public dialog: MatDialog,
        private route: ActivatedRoute,
        private userService: UserService,
        private translate: TranslateService,
        private notification: NotificationService,
        public configurationService: ConfigurationService,
        private readonly store: Store<AppState>,

    ) {
        this.showPbaInstallMenuItem = this.configurationService.showPbaInstallMenuItem;
        this.showPbaWipeMenuItem = this.configurationService.showPbaWipeMenuItem;
        this.showPbaDisableMenuItem = this.configurationService.showPbaDisableMenuItem;
        this.containsPba = this.configurationService.showPbaDisableMenuItem;
        this.containsFde = this.configurationService.showPbaDisableMenuItem;
        this.showAdmin = this.configurationService.showPbaDisableMenuItem;
        this.agentOnEndpoint = this.configurationService.showPbaDisableMenuItem;
        this.translate.get(['END_ASSIGN', 'END_LOCK', 'END_UNLOCK', 'END_ENDPOINTS', 'END_ENDNAME', 'END_NAME_FILTER',
            'END_D3EV', 'END_MANLOCK', 'END_ACTLOCK', 'END_OS', 'END_LAST_LOGGED_IN', 'END_GROUP', 'END_POLICY',
            'END_SEEN', 'END_NODATA', 'TITLE_ENDPOINTS', 'END_LOCK_TITLE', 'END_LOCK_CONTENT',
            'END_UNLOCK_TITLE', 'END_UNLOCK_CONTENT', 'END_LOCKED', 'END_UNLOCKED', 'END_STANDING_GUARD', 'END_LAST_LOGGED_IN_TIME',
            'END_THREAT_DETECT', 'END_PEND_LOCK', 'END_PEND_UNLOCK', 'D3ED_CONNECTED', 'D3ED_NEVER', 'YES', 'NO', 'D3ED_AVAILABLE', 'D3ED_NOT_AVAIL',
            'SELECTOR_ALL_ENDPOINTS']).subscribe(vals => {
            this.fAssignToGroupButton = vals['END_ASSIGN'];
            this.fLockButton = vals['END_LOCK'];
            this.fUnlockButton = vals['END_UNLOCK'];
            this.fEndpoints = vals['END_ENDPOINTS'];
            this.fEndpointName = vals['END_ENDNAME'];
            this.fNameFilter = vals['END_NAME_FILTER'];
            this.fD3EV = vals['END_D3EV'];
            this.fManualLock = vals['END_MANLOCK'];
            this.fActiveLock = vals['END_ACTLOCK'];
            this.fOS = vals['END_OS'];
            this.fLastLoggedIn = vals['END_LAST_LOGGED_IN'];
            this.fLastLoggedInTime = vals['END_LAST_LOGGED_IN_TIME'];
            this.fGroup = vals['END_GROUP'];
            this.fPolicy = vals['END_POLICY'];
            this.fLastSeen = vals['END_SEEN'];
            this.fNoData = vals['END_NODATA'];
            this.fLockTitle = vals['END_LOCK_TITLE'];
            this.fLockContent = vals['END_LOCK_CONTENT'];
            this.fUnlockTitle = vals['END_UNLOCK_TITLE'];
            this.fUnlockContent = vals['END_UNLOCK_CONTENT'];
            this.fLocked = vals['END_LOCKED'];
            this.fUnlocked = vals['END_UNLOCKED'];
            this.fStandingGuard = vals['END_STANDING_GUARD'];
            this.fThreatDetected = vals['END_THREAT_DETECT'];
            this.fPendingLock = vals['END_PEND_LOCK'];
            this.fPendingUnlock = vals['END_PEND_UNLOCK'];
            this.fConnected = vals['D3ED_CONNECTED'];
            this.fNever = vals['D3ED_NEVER'];
            this.fYes = vals['YES'];
            this.fNo = vals['NO'];
            this.fAvail = vals['D3ED_AVAILABLE'];
            this.fNotAvail = vals['D3ED_NOT_AVAIL'];
            this.title = vals['TITLE_ENDPOINTS'];

            this.initializeColumnDefinitions();

            this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_ALL_ENDPOINTS']);

        });
        this.headerService.setContextHelp(['help.endpoints']);
        this.headerService.setTitle(this.title);
        this.headerService.setSubtitle('');
        this.headerService.setIcon('mdi-monitor');
        this.headerService.setNavMenuText('');
        this.headerService.showUserFilter(true);
        this.headerService.showRefreshButton(true);
        this.headerService.refresh$.pipe(untilDestroyed(this)).subscribe(refresh => {
            if (refresh) {
                this.getEndpoints(true);
            }
        });

        const endpoints_target = localStorage.getItem('endpoints_target');
        if (endpoints_target !== undefined && endpoints_target != null) {
            this.selectedTarget = endpoints_target;
            this.setTargetColumnIndex();
        }

        this.headerService.orgSelectedChanged
            .pipe(untilDestroyed(this))
            .subscribe(value => {
                this.selectedOrgId = value;
                this.selectedSetId = 0;
                this.selectedPolicyId = 0;
                this.queryPageStart = 0;
                this.totalResults = 0;
                this.getUser();
            });

        this.route.queryParams.subscribe(params => {
            const deviceSetParam = params['deviceSetId'];
            if (deviceSetParam !== undefined && deviceSetParam != null) {
                this.selectedSetId = parseInt(deviceSetParam, 10);
            }
            const compliance = params['bitlocker_compliance'];
            switch (compliance?.toLowerCase()) {
                case 'compliant':
                    this.selectedBitlockerCompliance = BitLockerCompliance.COMPLIANT;
                    break;
                case 'non-compliant':
                    this.selectedBitlockerCompliance = BitLockerCompliance.NON_COMPLIANT;
                    break;
            }
            const endpoints_target = params['endpoints_target']
            if(!!endpoints_target) {
                this.setSelectedTarget(endpoints_target)
            }
        });
    }


    ngOnInit(): void {
        this.nameFilterForm.valueChanges
            .pipe(debounceTime(350),
                untilDestroyed(this))
            .subscribe((nameFilterValue) => {
                this.searchValue = nameFilterValue;
                this.getEndpoints(true);
            });

        this.totalResults = null;
    }

    getColumns(columns: IColumnChooserElement[],
               targetColumnsMD: IColumnChooserElement[])
        : IColumnChooserElement[] {

        const result: IColumnChooserElement[] = [];

        columns.forEach(column => {
            let found = null;
            targetColumnsMD.forEach(target => {
                if (target.value == column.value) {
                    found = target;
                }
            });
            if (found != null) {
                result.push({...found});
            } else {
                // @ts-ignore
                result.push({...column});
            }
        });
        return result;
    }

    onScrollHandler(event) {
        const tableViewHeight = event.target.offsetHeight; // viewport: ~500px
        const tableScrollHeight = event.target.scrollHeight; // length of all table
        const scrollLocation = event.target.scrollTop; // how far user scrolled

        // If the user has scrolled within 200px of the bottom, add more data
        const buffer = 600;
        let limit = tableScrollHeight - tableViewHeight - buffer;
        if (scrollLocation > limit && this.gettingEndpoints === false && this.queryPageStart <= this.totalResults) {
            this.queryPageStart += this.queryPageSize;
            this.getEndpoints();
        }
    }

    getUser() {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        this.userService.getUser(selectedOrg)
            .pipe(untilDestroyed(this))
            .subscribe(
                user => {
                    this.showPolicies = user.org_type.value === OrgType.CUSTOMER;
                    this.orgType = user.org_type.value;
                    if (user.org_type.value === OrgType.CUSTOMER) {
                        this.getEndpointsConfig();
                    } else {
                        this.endpoints = [];
                        this.endpointsDataLoaded = true;
                    }
                });
    }

    getEndpointsConfig() {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService
            .getEndpointsConfig(selectedOrg)
            .pipe(
                catchError(() => of([])),
                untilDestroyed(this))
            .subscribe(response => {
                this.deviceSetsEnabled = response['deviceSetsEnabled'];
                if(!this.configurationService.showAgentMenus) {
                    this.targets = ['inventory', 'status', 'protections', 'encryption'];
                }

                this.handlePermissionDrivenAttrs();

                this.deviceSets = response['deviceSets'];
                this.policies = response['policies'];
                // add the (Default) indicator
                if (this.policies.length) {
                    this.policies[this.policies.length - 1]['name'] =
                        this.policies[this.policies.length - 1]['name'] + ' (Default)';
                }

                const rstFeatureFlag = authHelper.getFeatureFlagFromToken('rst');
                if (rstFeatureFlag) {
                    this.pinResetEnabled = true;
                    this.pinResetVersion = response['pinResetVersion'];
                }

                this.getEndpoints(true);
        });
    }

    private handlePermissionDrivenAttrs() {
        if (this.deviceSetsEnabled) {
            this.showColumn('group');
            this.showColumn('policy');
        } else {
            this.hideColumn('group');
            this.hideColumn('policy');
        }

        if (!this.showPolicies) {
            this.hideColumn('policy');
        } else {
            this.showColumn('policy');
        }
    }

    getEndpoints(clearList: boolean = false) {
        if (clearList) {
            this.endpoints = [];
            this.queryPageStart = 0;
            this.totalResults = 0;
        }

        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        if (this.orgType !== OrgType.CUSTOMER && !selectedOrg) {
            this.endpoints = [];
            this.endpointsDataLoaded = true;
            this.totalResults = 0;
            return;
        }

        const query = this.getQuery();
        query['include_group_info'] = true;
        query['checkCompliance'] = true;
        this.endpointsDataLoaded = false;
        this.gettingEndpoints = true;

        this.d3eService
            .get_home_devices(query)
            .pipe(
                catchError(() => of([])),
                untilDestroyed(this))
            .subscribe((homeDevicesResponse: IEndpointsResponse) => {
                    const now = dayjs().utc();
                    const endpointRowList = [] as { checked: boolean; endpointData: IEndpoint }[];
                    const work_list = homeDevicesResponse.devices;
                    if (work_list) {
                        work_list.forEach(endpoint => {
                            LicenseMethods.setLastSeen(endpoint, now, LicenseMethods.disconnectThreshold);
                            // Translate the computed value
                            if (endpoint?.last_seen === 'Connected') {
                                endpoint.last_seen = this.fConnected;
                            } else if (endpoint?.last_seen === 'Never') {
                                endpoint.last_seen = this.fNever;
                            }
                            endpoint.os = LicenseMethods.getOsVersion(endpoint);
                            endpoint.os_version = LicenseMethods.getOsBuildVersion(endpoint);
                            endpoint.tpm_version = LicenseMethods.getTPMVersion((endpoint));
                            endpoint.manualLock = this.fUnlocked;
                            endpoint.activeLock = this.fStandingGuard;
                            if (endpoint.computer_name == null ||
                                endpoint.computer_name.length === 0) {
                                endpoint.computer_name = '...';  // this lets a link be created for endpoint details
                            }
                            if (endpoint.state != null) {
                                const deviceState = JSON.parse(endpoint.state.toString()) as IDeviceState;
                                if (deviceState.activeLock !== undefined && deviceState.activeLock != null) {
                                    for (let i = 0; i < deviceState.activeLock.length; i++) {
                                        const threat_id = deviceState.activeLock[i];
                                        if (threat_id === 1001) {
                                            endpoint.manualLock = this.fLocked;
                                        } else {
                                            endpoint.activeLock = this.fThreatDetected;
                                        }
                                    }
                                }
                            }

                            if (endpoint.pending_list != null) {
                                endpoint.pending_list = JSON.parse(endpoint.pending_list.toString()) as IPendingList;
                                endpoint.pending_list['pending'].forEach(pending_item => {
                                    // This overwrites the values up above
                                    if (pending_item['sensor_id'] === 1001) {
                                        if (pending_item['enabled'] == true) { // do not change this to === , it needs to be ==
                                            endpoint.manualLock = this.fPendingLock;
                                        } else {
                                            endpoint.manualLock = this.fPendingUnlock;
                                        }
                                    }
                                });
                            }
                            endpoint.protectorDisplay = this.getProtectorDisplay(endpoint);
                            endpoint.fixedDrivesEncryptionDisplay = this.getFixedDrivesEncryptionDisplay(endpoint);

                            const endpointRow = {checked: this.selectAllChecked, endpointData: endpoint};
                            endpointRowList.push(endpointRow);

                        }); // end forEach
                    } // end if (work_list)
                    this.endpointsDataLoaded = true;
                    if (!clearList) {
                        this.endpoints = [...this.endpoints, ...endpointRowList];
                    } else {
                        this.endpoints = endpointRowList;
                    }
                    this.checkSelects();
                    this.totalResults = homeDevicesResponse.total;
                    this.gettingEndpoints = false;
                }, // end subscribe
                error => {
                    // TODO: Do something better with the error, like notify the user.
                    console.error(`Error retrieving endpoints: ${error}`);
                    this.gettingEndpoints = false;
                });
    }

    isAvailable(value: boolean) {
        if (value) {
            return this.fAvail;
        }
        return '';
    }


    getQuery(paging = true) {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        const selectedSets = [];

        if (this.selectedSetId !== 0) {
            selectedSets.push(this.selectedSetId);
        }

        const selectedPolicies = [];
        if (this.selectedPolicyId !== 0) {
            selectedPolicies.push(this.selectedPolicyId);
        }

        const query = {
            'computer_name': this.searchValue,
            'where': {'deactivated': 0},
            'sets': selectedSets,
            'policies': selectedPolicies,
            'selectedOrgId': selectedOrg,
            'bitlocker_compliance': this.selectedBitlockerCompliance,
        };

        if (this.sortAttrs.length) {
            const sorts = [];
            for (let i = 0; i < this.sortAttrs.length; i++) {
                const sort = {
                    'attr': this.sortAttrs[i]['sortAttr'],
                    'desc': (this.sortAttrs[i]['sortOrder'] === 'desc')
                };
                switch (sort['attr']) {
                    case 'deviceName':
                        sort['attr'] = 'computer_name';
                        break;
                    case 'bios_serial':
                        sort['attr'] = 'bios_serial';
                        break;
                    case 'agent_version':
                        sort['attr'] = 'agent_version';
                        break;
                    case 'version':
                        sort['attr'] = 'version';
                        break;
                    case 'lastSeen':
                        sort['attr'] = 'last_check_dttm';
                        break;
                    case 'lastLoggedIn':
                        sort['attr'] = 'last_logged_in_user';
                        break;
                    case 'lastLoggedInTime':
                        sort['attr'] = 'last_logged_in_time';
                        break;
                    case 'os':
                        sort['attr'] = 'os_version';
                        break;
                    case 'manufacturer':
                        sort['attr'] = 'manufacturer';
                        break;
                    case 'model':
                        sort['attr'] = 'model';
                        break;
                    default:
                        continue;
                }
                sorts.push(sort);
            }
            if (sorts.length) {
                query['sorts'] = sorts;
            }
        }

        this.endpointsDataLoaded = false;

        if (paging) {
            query['pageStart'] = this.queryPageStart;
            query['pageSize'] = this.queryPageSize;
        }


        return query;
    }

    selectAll() {
        this.checkResetButtonEnabled();

        const uncheckedCount = this.endpoints.reduce((a, e) => e.checked ? a : a + 1, 0);
        if (uncheckedCount > 0) {
            this.endpoints.forEach(e => e.checked = true);
            this.endpointsSelected = true;
        } else {
            this.endpoints.forEach(e => e.checked = false);
            this.endpointsSelected = false;
        }

        const selectedEndpoints = this.endpoints.filter(e => e.checked);

        this.containsPba = selectedEndpoints.length > 0 &&
            selectedEndpoints.some(endpoint => !!this.getPbaInfo(endpoint.endpointData));

        this.containsFde = selectedEndpoints.length > 0 &&
            selectedEndpoints.some(endpoint => !!this.getFdeVolume(endpoint.endpointData));

        this.singlePbaSelected = selectedEndpoints.length === 1 &&
            !!this.getPbaInfo(selectedEndpoints[0].endpointData);

        this.singleFdeSelected = selectedEndpoints.length === 1 &&
            !!this.getFdeVolume(selectedEndpoints[0].endpointData);

        this.agentOnEndpoint = selectedEndpoints.some(
            endpoint => (endpoint.endpointData?.agent_count ?? 0) > 0
        );

        this.isDataDefense = this.endpoints.length > 0 &&
            this.endpoints.every((e) => e.endpointData.version === '0.0.0');
    }

    checkSelects() {
        this.checkResetButtonEnabled();

        const selectedEndpoints = this.endpoints.filter(e => e.checked);

        const checkedCount = selectedEndpoints.length;

        this.endpointsSelected = checkedCount > 0;
        this.selectAllChecked =
            this.endpoints.length > 0 &&
            checkedCount === this.endpoints.length;

        this.containsPba = selectedEndpoints.length > 0 &&
            selectedEndpoints.some(endpoint => !!this.getPbaInfo(endpoint.endpointData));

        this.containsFde = selectedEndpoints.length > 0 &&
            selectedEndpoints.some(endpoint => !!this.getFdeVolume(endpoint.endpointData));

        this.singlePbaSelected = selectedEndpoints.length === 1 &&
            !!this.getPbaInfo(selectedEndpoints[0].endpointData);

        this.singleFdeSelected = selectedEndpoints.length === 1 &&
            !!this.getFdeVolume(selectedEndpoints[0].endpointData);

        this.agentOnEndpoint = selectedEndpoints.some(
            endpoint => (endpoint.endpointData?.agent_count ?? 0) > 0
        );

        this.isDataDefense = this.endpoints.length > 0 &&
            this.endpoints.every((e) => e.endpointData.version !== '0.0.0');

    }

    private checkResetButtonEnabled() {
        if (this.pinResetEnabled && this.pinResetVersion) {
            const versions = [];
            this.endpoints.forEach(endpoint => {
                if (endpoint['checked']) {
                    const version = endpoint['endpointData'].version;
                    versions.push(version);
                }
            });

            if (versions.length === 1 && versions[0]) {
                // version may look like "3.4.0 (arbitrary text)"
                const cleanVersion = versions[0].split('(')[0]; // clean up possible text from end of version string
                // @ts-ignore
                const comparison = compareLicenseVersions(this.pinResetVersion, cleanVersion);
                if (comparison < 1) {
                    this.resetableSelected = true;
                    return;
                }
            }
        }
        this.resetableSelected = false;
    }

    checkSelectAllLater() {
        setTimeout(this.checkSelects.bind(this), 0);
    }

    lockSelectedEndpoints() {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        const dialogRef = this.dialog.open(LockEndpointsDialogComponent, {width: '450px'});
        dialogRef.afterClosed().subscribe((result: ILockEndpointsDialogResult) => {
            if (result) {
                if (this.selectAllChecked) {
                    this.sendLockRequestByQuery(selectedOrg, true, result.allowUserUnlock);
                } else {
                    this.sendLockRequest(selectedOrg, true, result.allowUserUnlock);
                }
            }
        });
    }

    unlockSelectedEndpoints() {
        const title = this.fUnlockTitle;
        const content = this.fUnlockContent;
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
                title: title,
                content: content,
                rejectBtnName: this.translate.instant('CANCEL'),
                acceptBtnName: this.translate.instant('CONFIRM'),
                showRejectBtn: true,
            },
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                if (this.selectAllChecked) {
                    this.sendLockRequestByQuery(selectedOrg, false, false);
                } else {
                    this.sendLockRequest(selectedOrg, false, false);
                }
            }
        });
    }

    sendLockRequestByQuery(selectedOrgId, enabled: boolean, allowUserDeactivate: boolean) {
        const query = this.getQuery(false);
        this.d3eService.lockEndpointsByQuery(selectedOrgId, query, enabled, allowUserDeactivate)
            .subscribe(() => {
                this.endpointsSelected = false;
                this.selectAllChecked = false;
                // to stop it from a delay, set all the selected to false.
                this.endpoints.forEach(endpoint => endpoint['checked'] = false);
                this.queryPageStart = 0;
                this.queryPageSize = 50;
                this.getEndpoints(true);
            });
    }

    sendLockRequest(selectedOrgId, enabled: boolean, allowUserDeactivate: boolean) {
        const licenses: number[] = [];
        this.endpoints.forEach(endpoint => {
            if (endpoint['checked']) {
                const license_id = endpoint['endpointData'].id;
                licenses.push(license_id);
            }
        });
        this.d3eService.lockEndpoints(selectedOrgId, licenses, enabled, allowUserDeactivate)
            .subscribe(() => {
                    this.endpointsSelected = false;
                    this.queryPageStart = 0;
                    this.queryPageSize = 50;
                    this.getEndpoints(true);
                }
            );

    }

    onSetChange(deviceId) {
        this.queryPageStart = 0;
        this.selectedSetId = deviceId;
        this.getEndpoints(true);
    }

    onPolicyChange(policyId) {
        this.queryPageStart = 0;
        this.selectedPolicyId = policyId;
        this.getEndpoints(true);
    }

    updateDisplayedColumns(columns: string[]) {
        this.columnsToDisplay = columns;
    }

    forceResetPolicy() {
        const licenseUUIDs = [];
        this.endpoints.forEach(endpoint => {
            if (endpoint['checked']) {
                const license_uuid = endpoint['endpointData'].uuid;
                licenseUUIDs.push(license_uuid);
            }
        });
        const licenses: IForcePolicyResetRequest = {'licenses': licenseUUIDs};
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
                title: this.translate.instant('force-policy-reset.confirmation-title'),
                content: this.translate.instant('force-policy-reset.confirmation-content'),
                rejectBtnName: this.translate.instant('NO'),
                acceptBtnName: this.translate.instant('YES'),
                showRejectBtn: true,
            }
        });
        dialogRef.afterClosed().pipe(
          switchMap((result) => {
              if (result) {
                  return this.d3eService.force_policy_reset(licenses);
              }
              return EMPTY;
          }),
            catchError(() => {
                this.notification.showError(this.translate.instant('D3ED_FORCE_RESET_ERROR'));
                this.queryPageStart = 0;
                this.getEndpoints(true);
                return EMPTY;
            }),
        ).subscribe(() => {
            this.notification.showSuccess(this.translate.instant('D3ED_FORCE_RESET_SELECTED_SUCCESS'));
        })
    }

    transferLicensesToSubscription() {
        const selectedEndpoints = this.endpoints
            .filter(o => o.checked)
            .map(e => e.endpointData);

        this.d3eService.get_subscriptions(this.selectedOrgId).pipe(
            switchMap(subscriptions => {
                const config = {
                    data: {
                        // Filter out only unexpired subscriptions, and if selectedOrgId is something other
                        // than empty or All (e.g. MSP selecting a single org), also filter to only matching group_ids
                        // TODO: It would be nice to not show a sub if all the endpoints already have it
                        subscriptions: subscriptions
                            .filter(s => (!this.selectedOrgId
                                    || (this.selectedOrgId === ALL_ORGS_ID)
                                    || (s.group_id.toString() === this.selectedOrgId))
                                && new Date(s.exp_timestamp) > new Date())
                            .sort((a, b) => a.exp_timestamp < b.exp_timestamp ? 1 : -1)
                    }
                };
                const dialogRef = this.dialog.open<TransferLicenseDialogComponent,
                    any, ISubscription>(TransferLicenseDialogComponent, config);
                return dialogRef.afterClosed();
            }),
            switchMap(subscription => {
                return from(selectedEndpoints.map(endpoint => {
                    return {subscription, endpoint};
                }));
            }),
            mergeMap(({subscription, endpoint}) => {
                if (!subscription || !endpoint || endpoint.sub_id === subscription.id) {
                    // Don't try to move to the license an endpoint already has (or if it was cancelled)
                    return EMPTY;
                } else {
                    return this.d3eService.move_license(subscription.id, endpoint.uuid);
                }
            }),
            reduce((acc, result) => acc + 1, 0)
        ).subscribe(count => {
            if (count > 0) {
                this.notification.showSuccess(this.translate.instant('SUCCESS'));
            }
            this.queryPageStart = 0;
            this.getEndpoints(true);
        });
    }

    deleteLicenses() {
        this.dialog.open(AlertDialogComponent, {
            data: {
                title: this.translate.instant('endpoints.delete-dialog.title'),
                content: this.translate.instant('endpoints.delete-dialog.content'),
                rejectBtnName: this.translate.instant('CANCEL'),
                acceptBtnName: this.translate.instant('CONTINUE'),
                showRejectBtn: true,
            }
        }).afterClosed().pipe(
          map(result => {
              if (result) {
                  return this.endpoints.filter(e => e.checked)
                    .map(e => this.d3eService.delete_endpoint(e.endpointData.uuid))
              }
              return [];
          }),
          mergeMap(obsArray => {
              if (obsArray.length === 0) {
                  return of([]); // Return an empty observable if no observables were created
              }
              return from(obsArray).pipe(
                bufferCount(4),
                concatMap(batch => forkJoin(batch)),
                toArray()
              );
          })
        ).subscribe((result) => {
            if (result.length > 0) {
                this.notification.showSuccess(this.translate.instant('endpoints.delete-successful'));
            }
            this.getEndpoints(true);
        });

    }

    deactivateLicenses() {
        const selectedEndpoints = this.endpoints
            .filter(o => o.checked)
            .map(e => e.endpointData);
        const dialogRef = this.dialog.open(AlertDialogComponent, {
            data: {
                title: this.translate.instant('endpoints.deactivate-dialog.title'),
                content: this.translate.instant('endpoints.deactivate-dialog.content', {count: selectedEndpoints.length}),
                rejectBtnName: this.translate.instant('CANCEL'),
                acceptBtnName: this.translate.instant('CONTINUE'),
                showRejectBtn: true,
            }
        });
        dialogRef.afterClosed().pipe(
            switchMap(confirmation => {
                if (confirmation) {
                    return from(selectedEndpoints);
                }
                return EMPTY;
            }),
            mergeMap(endpoint => {
                return this.d3eService.deactivate_license(endpoint.sub_id, endpoint.uuid);
            }, 4),
            reduce((acc, result) => {
                return acc + 1;
            }, 0),
            catchError(error => {
                this.notification.showError(this.translate.instant('endpoints.deactivate-error'));
                this.queryPageStart = 0;
                this.getEndpoints(true);
                return EMPTY;
            }),
        ).subscribe(count => {
                if (count > 0) {
                    this.notification.showSuccess(this.translate.instant('SUCCESS'));
                    this.queryPageStart = 0;
                    this.getEndpoints(true);
                }
            },
            error => {
                console.error('deactivating', error);
            });
    }

    resetEndpointPIN() {
        let selectedEndpoint = null;
        this.endpoints.forEach(endpoint => {
            if (endpoint['checked']) {
                selectedEndpoint = endpoint['endpointData'];
            }
        });

        if (!selectedEndpoint) {
            return;
        }

        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
                title: this.translate.instant('reset-pin.request.title'),
                titleRight: selectedEndpoint.computer_name,
                content: this.translate.instant('reset-pin.request.content', {'computer_name': selectedEndpoint.computer_name}),
                rejectBtnName: this.translate.instant('CANCEL'),
                acceptBtnName: this.translate.instant('CONTINUE'),
                showRejectBtn: true,
            },
        });
        dialogRef.afterClosed().pipe(
            mergeMap(result => {
                if (result) {
                    this.requestResetPIN(selectedEndpoint);
                }
                return EMPTY;
            }),
            catchError(error => this.errorDialog(selectedEndpoint.computer_name, error))
        ).subscribe();

    }

    private requestResetPIN(selectedEndpoint) {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        const copyText = this.translate.instant('reset-pin.copy-to-clipboard');
        this.d3eService.resetEndpoint(selectedOrg, selectedEndpoint.id)
            .pipe(untilDestroyed(this))
            .subscribe(result => {
                    const content1 = this.translate.instant('reset-pin.show.content1',
                        {'computer_name': selectedEndpoint.computer_name});

                    this.dialog.open(AlertClipboardCopyComponent, {
                        width: '450px',
                        data: {
                            title: this.translate.instant('reset-pin.show.title'),
                            titleRight: selectedEndpoint.computer_name,
                            preContent: content1,
                            clipboardContent: result['resetCode'],
                            copyButtonId: 'copyPinToClipboard',
                            clickText: copyText,
                            acceptBtnName: this.translate.instant('H_CLOSE'),
                            showRejectBtn: false,
                            parent: this,
                        },
                    });
                },
                error => {
                    this.errorDialog(selectedEndpoint.computer_name, error);
                }
            );
    }

    private getSelectedEndpoint(): IEndpoint | undefined {
        return this.endpoints.find(e => e.checked)?.endpointData;
    }

    private mergeUsersWithPendingActions(baseUsers: any[], pendingUsers: any[]): any[] {
        const seen = new Set<string>();
        const result: any[] = [];

        const baseUserMap = new Map<string, any>();
        for (const user of baseUsers) {
            if (user.username) {
                baseUserMap.set(user.username, { ...user });
            }
        }

        for (const pending of pendingUsers) {
            const username = pending.username;
            if (!username) continue;

            const base = baseUserMap.get(username) ?? {
                username: '',
                email: '',
                role: 0,
                disk_serial: '',
            };

            const merged: any = {
                ...base,
                ...pending,
            };

            if (!seen.has(username)) {
                seen.add(username);
                result.push(merged);
            }
        }

        for (const [username, user] of baseUserMap) {
            if (!seen.has(username)) {
                seen.add(username);
                result.push(user);
            }
        }

        return result;
    }

    managePbaUsers(): void {
        const endpoint = this.getSelectedEndpoint();
        if (!endpoint) return;

        const diskSerial = this.getPbaSerial(endpoint);
        if (!diskSerial) return;

        this.d3eService.getActionData(
            [endpoint.uuid],
            true
        ).subscribe({
            next: actionData => {
                const data = actionData.find(item => item.license_id === endpoint.id);
                if (!data) return;

                const combinedPbaUsers = this.mergeUsersWithPendingActions(
                    data.pba_users ?? [],
                    data.pending_pba_users ?? []
                );

                this.dialog.open<
                    ManageEndpointUserDialogComponent,
                    IManageEndpointUserDialogData
                >(ManageEndpointUserDialogComponent, {
                    data: {
                        title: 'Manage PBA Users',
                        userType: 'pba',
                        users: combinedPbaUsers,
                        biosSerial: endpoint.bios_serial,
                        endpointUuid: endpoint.uuid,
                        licenseId: endpoint.id,
                        endpointName: endpoint.computer_name,
                        diskSerial,
                        pwMin: data.pba_pw_min ?? 8,
                    },
                });
            },
            error: error => {
                this.notification.showError(`Failed to get PBA users: ${error}`);
            },
        });
    }

    manageFdeUsers(): void {
        const endpoint = this.getSelectedEndpoint();
        if (!endpoint) {
            return;
        }

        const diskSerial = this.getFdeVolume(endpoint)?.guid;
        if (!diskSerial) {
            return;
        }

        this.d3eService.getActionData(
            [endpoint.uuid],
            true
        ).subscribe({
            next: actionData => {
                const data = actionData.find(
                    item => item.license_id === endpoint.id
                );

                if (!data) return;

                const combinedFdeUsers = this.mergeUsersWithPendingActions(
                    data.fde_users ?? [],
                    data.pending_fde_users ?? []
                );

                this.dialog.open<
                    ManageEndpointUserDialogComponent,
                    IManageEndpointUserDialogData
                >(ManageEndpointUserDialogComponent, {
                    data: {
                        title: 'Manage FDE Users',
                        userType: 'fde',
                        users: combinedFdeUsers,
                        biosSerial: endpoint.bios_serial,
                        endpointUuid: endpoint.uuid,
                        licenseId: endpoint.id,
                        endpointName: endpoint.computer_name,
                        diskSerial,
                        pwMin: data.fde_pw_min ?? 8,
                    },
                });
            },
            error: error => {
                this.notification.showError(`Failed to get FDE users: ${error}`);
            },
        });
    }

    private showEnrollCodeError(error: unknown): void {
        this.notification.showError(`Failed to add enrollment codes, ${error}`);

        this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
                title: 'Enrollment Codes Not Added',
                content: 'The enrollment codes could not be added. Please try again.',
                acceptBtnName: this.translate.instant('OK'),
                showRejectBtn: false,
            },
        });
    }

    private showEnrollCodeDeleteError(error: unknown): void {
        this.notification.showError(`Failed to delete enrollment codes, ${error}`);

        this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
                title: 'Enrollment Codes Not Deleted',
                content: 'The enrollment codes could not be deleted. Please try again.',
                acceptBtnName: this.translate.instant('OK'),
                showRejectBtn: false,
            },
        });
    }

    private openEndpointActionDialog(
        action: ConsoleAction,
        endpointId: string,
        model: string,
        uuid: string,
        disk_serial: string,
    ): void {
        const data = getActionDialogData(
            action,
            endpointId,
            model,
        );

        this.dialog.open<
            ActionConfirmationDialogComponent,
            IActionConfirmationDialogData,
            IActionConfirmationDialogResult
        >(ActionConfirmationDialogComponent, {
            data,
        }).afterClosed().subscribe(result => {
            if (!result?.confirmed) {
                return;
            }

            this.d3eService.performConsoleAction(
                result.action,
                uuid,
                disk_serial,
                result.date,
                result.range,
            ).subscribe({
                next: () => {
                    console.log(
                        `Endpoint action ${result.action} succeeded`
                    );
                },
                error: error => {
                    this.notification.showError(`Endpoint action ${result.action} failed, ${error}`);
                },
            });

            console.log(result);
        });
    }

    private openEndpointClearActionsDialog(
        action: ConsoleAction,
        endpointId: string,
        model: string,
        uuid: string
    ): void {
        if (!endpointId || !model || !uuid) {
            this.notification.showError('Missing required parameters: endpointId, model, or uuid');
            return;
        }

        const data = getActionDialogData(
            action,
            endpointId,
            model
        );

        this.dialog.open<
            ActionConfirmationDialogComponent,
            IActionConfirmationDialogData,
            IActionConfirmationDialogResult
        >(ActionConfirmationDialogComponent, {
            data,
        }).afterClosed().subscribe(result => {
            if (!result?.confirmed) {
                return;
            }

            this.d3eService.performClearConsoleAction(
                action,
                uuid
            ).subscribe({
                next: () => {
                    console.log(
                        `Endpoint clear action ${action} succeeded`
                    );
                },
                error: error => {
                    this.notification.showError(`Endpoint clear action ${result.action} failed, ${error}`);
                },
            });

            console.log(result);
        });
    }

    private openEnrollCodesAddedDialog(
        request: IEnrollCodesDialogResult,
        response: IAddEnrollCodesResponse,
    ): void {
        const addedCodes = (response.codes ?? []).map(code => {
            const requestCode = request.codes.find(item =>
                item.licenseId === code.endpointId &&
                item.diskSerial === code.diskSerial
            );

            return {...code, code: requestCode?.enroll_code_id ?? ''};
        });

        const failedCodes = (response.failed_codes ?? []).map(code => {
            const requestCode = request.codes.find(item =>
                item.licenseId === code.endpointId &&
                item.diskSerial === code.diskSerial
            );

            return {...code, code: requestCode?.enroll_code_id ?? ''};
        });

        let title = 'Enrollment Codes Added';

        if (addedCodes.length === 0 && failedCodes.length > 0) {
            title = 'Enrollment Codes Could Not Be Added';
        } else if (failedCodes.length > 0) {
            title = 'Enrollment Codes Partially Added';
        }

        this.dialog.open<
            EnrollCodesAddedDialogComponent,
            IEnrollCodesAddedDialogOptions
        >(EnrollCodesAddedDialogComponent, {
            data: {
                title,
                codes: addedCodes,
                failedCodes,
                success: response.success,
                partialSuccess: response.partial_success,
                message: response.message,
            },
        });
    }

    private addEnrollCodes(type: 'pba' | 'fde'): void {
        const selectedEndpoints = this.endpoints.filter(endpoint =>
            endpoint.checked &&
            (
                type === 'pba'
                    ? !!this.getPbaInfo(endpoint.endpointData)
                    : !!this.getFdeVolume(endpoint.endpointData)
            )
        );

        if (selectedEndpoints.length === 0) {
            return;
        }

        const endpointIds = selectedEndpoints.map(
            ({endpointData}) => endpointData.uuid
        );

        this.d3eService.getActionData(endpointIds).pipe(
            switchMap(actionData => {
                const actionDataById = new Map(
                    actionData.map(data => [
                        data.license_id,
                        data,
                    ])
                );

            const title = type === 'pba'
                ? 'Add PBA Enrollment Codes'
                : 'Add FDE Enrollment Codes';

            const endpoints = selectedEndpoints.map(({endpointData}) => {
                const data = actionDataById.get(
                    endpointData.id
                );

                return {
                    licenseId: endpointData.id,
                    biosSerial: endpointData.bios_serial,
                    endpointName: endpointData.computer_name,
                    diskSerial: type === 'pba'
                        ? this.getPbaSerial(endpointData)
                        : this.getFdeVolume(endpointData)?.guid,
                    pwMin: type === 'pba'
                        ? data?.pba_pw_min ?? 8
                        : data?.fde_pw_min ?? 8,
                };
            });

            return this.dialog.open<
                EnrollCodesDialogComponent,
                IEnrollCodesDialogOptions,
                IEnrollCodesDialogResult
            >(EnrollCodesDialogComponent, {
                data: {
                    type,
                    title,
                    endpoints,
                },
            }).afterClosed();
        }),
        switchMap(result => {
            if (!result) {
                return EMPTY;
            }

                return this.d3eService.addEnrollmentCodes(result).pipe(
                    map((response: IAddEnrollCodesResponse) => ({
                        request: result,
                        response,
                    })),
                    catchError(error => {
                        const response =
                            error.error as IAddEnrollCodesResponse;

                        if (
                            response &&
                            Array.isArray(response.codes) &&
                            Array.isArray(response.failed_codes)
                        ) {
                            return of({
                                request: result,
                                response,
                            });
                        }

                        // Failure outside of adding enroll code errors
                        this.showEnrollCodeError(error);
                        return EMPTY;
                    })
                );
            })
        ).subscribe(({request, response}) => {
            this.openEnrollCodesAddedDialog(request, response);
        });
    }

    private removeEnrollCodes(type: 'pba' | 'fde'): void {
        const selectedEndpoints = this.endpoints.filter(endpoint =>
            endpoint.checked &&
            (
                type === 'pba'
                    ? !!this.getPbaInfo(endpoint.endpointData)
                    : !!this.getFdeVolume(endpoint.endpointData)
            )
        );

        if (selectedEndpoints.length === 0) {
            return;
        }

        const product = type.toUpperCase();

        const endpointText = selectedEndpoints.length === 1
            ? '1 endpoint'
            : `${selectedEndpoints.length} endpoints`;

        this.dialog.open<
            ActionConfirmationDialogComponent,
            IActionConfirmationDialogData,
            IActionConfirmationDialogResult
        >(ActionConfirmationDialogComponent, {
            data: {
                action: type === 'pba' 
                    ? ConsoleAction.REMOVE_ENROLLCODES_PBA 
                    : ConsoleAction.REMOVE_ENROLLCODES_FDE,
                title: `Remove ${product} Enrollment Codes`,
                content:
                    `This will remove all ${product} enrollment codes from ` +
                    `the selected ${endpointText}. Are you sure you want to ` +
                    `continue?`,
                acceptBtnName: 'Remove',
                rejectBtnName: 'Cancel',
            },
        }).afterClosed().pipe(
            switchMap(result => {
                if (!result?.confirmed) {
                    return EMPTY;
                }

                const diskSerials = selectedEndpoints
                    .map(({endpointData}) =>
                        type === 'pba'
                            ? this.getPbaSerial(endpointData)
                            : this.getFdeVolume(endpointData)?.guid
                    )
                    .filter(
                        (diskSerial): diskSerial is string =>
                            diskSerial !== null
                    );

                return this.d3eService.deleteEnrollmentCodes({
                    type,
                    diskSerials,
                });
            })
        ).subscribe({
            next: response => {
                this.dialog.open<
                    AlertDialogComponent,
                    IAlertDialogData
                >(AlertDialogComponent, {
                    data: {
                        title: 'Enrollment Codes Set for Removal',
                        content: `${product} enrollment codes have been set for removal.`,
                        unsafeHTML: '',
                        additionalContent: [],
                        acceptBtnName: 'OK',
                        rejectBtnName: '',
                        showRejectBtn: false,
                    },
                });
            },
            error: error => {
                this.showEnrollCodeDeleteError(error);
            },
        });
    }

    pbaRemoveEnrollCodes(): void {
        this.removeEnrollCodes('pba');
    }

    fdeRemoveEnrollCodes(): void {
        this.removeEnrollCodes('fde');
    }

    pbaAddEnrollCodes() {
        this.addEnrollCodes('pba');
    }

    fdeAddEnrollCodes() {
        this.addEnrollCodes('fde');
    }

    pbaWipe() {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getPbaSerial(selectedEndpoint);

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.ERASE_PBA,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    pbaEnable(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getPbaSerial(selectedEndpoint);

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.REACTIVATE_PBA,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    pbaDisable(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getPbaSerial(selectedEndpoint);

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.DEACTIVATE_PBA,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    fdeDisable(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getFdeVolume(selectedEndpoint)?.guid;

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.DEACTIVATE_FDE,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    fdeEnable(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getFdeVolume(selectedEndpoint)?.guid;

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.REACTIVATE_FDE,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    lockPbaRanges(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getPbaSerial(selectedEndpoint);

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.LOCK_PBA_RANGES,
            endpoint,
            model,
            uuid,
            diskSerial, 
        );
    }

    unlockPbaRanges(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getPbaSerial(selectedEndpoint);

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.UNLOCK_PBA_RANGES,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    pbaWipeAfter(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getPbaSerial(selectedEndpoint);

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.SET_PBA_ERASE_AFTER,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    clearPbaWipeAfter(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getPbaSerial(selectedEndpoint);

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.CLEAR_PBA_ERASE_AFTER,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    fdeWipe(): void {
        const selectedEndpoint = this.getSelectedEndpoint();
        const endpoint = selectedEndpoint?.bios_serial;
        const model = selectedEndpoint?.model;
        const uuid = selectedEndpoint?.uuid;

        if (!selectedEndpoint || !endpoint || !model || !uuid) {
            return;
        }

        const diskSerial = this.getFdeVolume(selectedEndpoint)?.guid;

        if(!diskSerial)
            return;

        this.openEndpointActionDialog(
            ConsoleAction.ERASE_FDE,
            endpoint,
            model,
            uuid,
            diskSerial,
        );
    }

    pbaClearAllActions(): void {
        const endpoint = this.getSelectedEndpoint()?.bios_serial;
        const model = this.getSelectedEndpoint()?.model;
        const uuid = this.getSelectedEndpoint()?.uuid;

        if (!endpoint || !model || !uuid) {
            return;
        }

        this.openEndpointClearActionsDialog(
            ConsoleAction.CLEAR_ALL_PBA_ACTIONS,
            endpoint,
            model,
            uuid,
        );
    }

    fdeClearAllActions(): void {
        const endpoint = this.getSelectedEndpoint()?.bios_serial;
        const model = this.getSelectedEndpoint()?.model;
        const uuid = this.getSelectedEndpoint()?.uuid;

        if (!endpoint || !model || !uuid) {
            return;
        }

        this.openEndpointClearActionsDialog(
            ConsoleAction.CLEAR_ALL_FDE_ACTIONS,
            endpoint,
            model,
            uuid,
        );
    }

    // requestPbaUpgrade() {
    //     const licenseIds = this.endpoints.filter(e => e.checked).map(e => e.endpointData.id);
    //     const dialogRef = this.dialog.open(AlertDialogComponent, {
    //         data: {
    //             title: this.translate.instant('endpoints.install-pba-dialog.title'),
    //             content: this.translate.instant('endpoints.install-pba-dialog.content', {count: licenseIds.length}),
    //             rejectBtnName: this.translate.instant('CANCEL'),
    //             acceptBtnName: this.translate.instant('CONTINUE'),
    //             showRejectBtn: true,
    //         }
    //     });
    //     dialogRef.afterClosed().pipe(
    //       withLatestFrom(this.headerService.orgSelectedChanged),
    //       switchMap(([result, orgId]: [boolean, string]) => {
    //           if (result) {
    //               orgId = (!!orgId && orgId != 'All') ? orgId : undefined;
    //               return this.d3eService.requestInstallPba(orgId, licenseIds);
    //           }
    //           return EMPTY;
    //       })
    //     ).subscribe()
    // }

    errorDialog(computer_name, errorText): Observable<any> {
        const dialogRef: MatDialogRef<AlertDialogComponent> = this.dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
                title: this.translate.instant('reset-pin.error.title'),
                content: this.translate.instant('reset-pin.error.content', {
                    'computer_name': computer_name,
                    'error_text': errorText
                }),
                acceptBtnName: this.translate.instant('OK')
            },
        });
        return dialogRef.afterClosed();
    }

    sortColumn(event) {
        const sort = {
            'sortAttr': event.active,
            'sortOrder': event.direction
        };

        this.sortAttrs = [sort];

        // changing sorting changes page start
        this.queryPageStart = 0;

        this.getEndpoints(true);

    }

    private initializeColumnDefinitions() {
        this.queryColumnNames = {
            'deviceName': 'computer_name',
            'lastLoggedIn': 'last_logged_in_user',
            'lastLoggedInTime': 'last_logged_in_time',
            'lastSeen': 'last_check_dttm',

        };

        this.columns = [
            {title: 'select', value: 'checkbox', selected: true, hidden: true,},
            {title: this.fEndpointName, value: 'deviceName', selected: false, hidden: false,},
            {title: 'Endpoint ID', value: 'bios_serial', selected: false, hidden: false,},
            {title: this.fD3EV, value: 'version', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.agent_version'), value: 'agent_version', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.products'), value: 'products', selected: false, hidden: false,},
            {title: this.fManualLock, value: 'manualLock', selected: false, hidden: false,},
            {title: this.fActiveLock, value: 'activeLock', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.os'), value: 'os', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.os-version'), value: 'os_version', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.manufacturer'), value: 'manufacturer', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.model'), value: 'model', selected: false, hidden: false,},
            {title: this.fLastLoggedIn, value: 'lastLoggedIn', selected: false, hidden: false,},
            {title: this.fLastLoggedInTime, value: 'lastLoggedInTime', selected: false, hidden: false,},
            {title: this.fGroup, value: 'group', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.policy-compliance'), value: 'policy_compliance', selected: false, hidden: true,},
            {title: this.translate.instant('endpoints.col.licensing'), value: 'licensing', selected: false, hidden: false,},
            {title: this.fPolicy, value: 'policy', selected: false, hidden: true,},
            {title: this.fLastSeen, value: 'lastSeen', selected: false, hidden: false,},
            {title: this.translate.instant('ORG_ORG'), value: 'organization', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.org-email'), value: 'orgEmail', selected: false, hidden: false, },
            {title: this.translate.instant('END_SUBSCRIPTION_ID'), value: 'subscription_id', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.secure-drive'), value: 'secure_drive_count', selected: false, hidden: false,},
            {title: 'Agent', value: 'agent_count', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.pba'), value: 'pba_count', selected: false, hidden: false,},
            {title: 'FDE', value: 'fde_count', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.folder'), value: 'folder_count', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.file-type'), value: 'file_type_count', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.protection'), value: 'protectors', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.tpm-version'), value: 'tpm_version', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.os-volume'), value: 'os_volume_encrypted', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.os-encryption'), value: 'os_encryption', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.fixed-drives'), value: 'fixed_drives_encrypted', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.fixed-drive-encryption'), value: 'fixed_drives_encryption', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.bitlocker-key-available'), value: 'bitlocker_key_available', selected: false, hidden: false,},
            {title: this.translate.instant('endpoints.col.bitlocker-compliance'), value: 'bitlocker_compliance', selected: false, hidden: false,},
        ];

        this.columns.forEach(column => {
            this.queryColumnNames[column.value] = column.value;
        });

        this.queryColumnNames['deviceName'] = 'computer_name';
        this.queryColumnNames['lastLoggedIn'] = 'last_logged_in_user';
        this.queryColumnNames['lastLoggedInTime'] = 'last_logged_in_time';
        this.queryColumnNames['lastSeen'] = 'last_check_dttm';
        this.queryColumnNames['group'] = 'device_set_name';
        this.queryColumnNames['policy'] = 'policy_name';


        // NOTE!
        // Fore each of the targets below, an entry should also be included in this.columns above!

        const inventoryColumns = [
            {saveId: 'endpoints-inventory', title: 'select', value: 'checkbox', selected: true, hidden: true,},
            {title: this.fEndpointName, value: 'deviceName', selected: true, hidden: false,},
            {title: this.fD3EV, value: 'version', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.os'), value: 'os', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.manufacturer'), value: 'manufacturer', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.model'), value: 'model', selected: true, hidden: false,},
            {title: this.fGroup, value: 'group', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.policy-compliance'), value: 'policy_compliance', selected: true, hidden: false,},
            {title: this.fPolicy, value: 'policy', selected: true, hidden: false,},
        ];

        const agentColumns = [
            {saveId: 'agents-inventory', title: 'select', value: 'checkbox', selected: true, hidden: true,},
            {title: this.fEndpointName, value: 'deviceName', selected: true, hidden: false,},
            {title: 'Endpoint ID', value: 'bios_serial', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.agent_version'), value: 'agent_version', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.os'), value: 'os', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.manufacturer'), value: 'manufacturer', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.model'), value: 'model', selected: true, hidden: false,},
            {title: this.fGroup, value: 'group', selected: true, hidden: false,},
            {title: this.translate.instant('endpoints.col.policy-compliance'), value: 'policy_compliance', selected: true, hidden: false,},
            {title: this.fPolicy, value: 'policy', selected: true, hidden: false,},
        ];        

        const statusColumns = [
            {saveId: 'endpoints-status', title: 'select', value: 'checkbox', selected: true, hidden: true, },
            {title: this.fEndpointName, value: 'deviceName', selected: true, hidden: false, },
            {title: this.fManualLock, value: 'manualLock', selected: true, hidden: false, },
            {title: this.fActiveLock, value: 'activeLock', selected: true, hidden: false, },
            {title: this.fLastLoggedIn, value: 'lastLoggedIn', selected: true, hidden: false, },
            {title: this.fLastLoggedInTime, value: 'lastLoggedInTime', selected: true, hidden: false, },
            {title: this.fLastSeen, value: 'lastSeen', selected: true, hidden: false, },

        ]

        const protectionColumns = [
            {saveId: 'endpoints-protection', title: 'select', value: 'checkbox', selected: true, hidden: true, },
            {title: this.fEndpointName, value: 'deviceName', selected: true, hidden: false, },
            {title: this.translate.instant('endpoints.col.secure-drive'), value: 'secure_drive_count', selected: true, hidden: false, },
            {title: 'Agent', value: 'agent_count', selected: true, hidden: false, },
            {title: this.translate.instant('endpoints.col.pba'), value: 'pba_count', selected: true, hidden: false, },
            {title: 'FDE', value: 'fde_count', selected: true, hidden: false, },
            {title: this.translate.instant('endpoints.col.folder'), value: 'folder_count', selected: true, hidden: false, },
            {title: this.translate.instant('endpoints.col.file-type'), value: 'file_type_count', selected: true, hidden: false, },

        ];

        const encryptionColumns = [
            {saveId: 'endpoints-encryption', title: 'select', value: 'checkbox', selected: true, hidden: true, },
            {title: this.fEndpointName, value: 'deviceName', selected: true, hidden: false,},
            {
                title: this.translate.instant('endpoints.col.protection'),
                value: 'protectors',
                selected: true,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.os-volume'),
                value: 'os_volume_encrypted',
                selected: true,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.tpm-version'),
                value: 'tpm_version',
                selected: false,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.os-version'),
                value: 'os_version',
                selected: false,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.os-encryption'),
                value: 'os_encryption',
                selected: true,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.fixed-drives'),
                value: 'fixed_drives_encrypted',
                selected: true,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.fixed-drive-encryption'),
                value: 'fixed_drives_encryption',
                selected: true,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.bitlocker-key-available'),
                value: 'bitlocker_key_available',
                selected: true,
                hidden: false,
            },
            {
                title: this.translate.instant('endpoints.col.bitlocker-compliance'),
                value: 'bitlocker_compliance',
                selected: true,
                hidden: false,
            },
        ];

        // the index of this array must line up with the index of the target field.
        this.targetColumnsMetadata = [
            this.getColumns(this.columns, agentColumns),            
            this.getColumns(this.columns, inventoryColumns),
            this.getColumns(this.columns, statusColumns),
            this.getColumns(this.columns, protectionColumns),
            this.getColumns(this.columns, encryptionColumns)
        ];
        this.targetColumns = [];
        this.targetColumnsMetadata[this.targetColumnsIndex].forEach( targetColumn => {
            this.targetColumns.push( {...targetColumn});
        });
    }

    getProtectorDisplay(endpoint: IEndpoint): string {
        const protectors = [];
        if ( endpoint.protectors != null ) {
            endpoint.protectors.forEach(protector => {
                protectors.push(getProtectorDisplayValue(protector, this.translate));
            });
        }
        return protectors.join(', ');
    }

    getVolumeProtectionDisplay(isProtected: boolean): string {
        if ( isProtected ) {
            return this.translate.instant('endpoints.partitions.volume_protection_enum.protected');
        }
        return this.translate.instant('endpoints.partitions.volume_protection_enum.unprotected');
    }

    private getFixedDrivesEncryptionDisplay(endpoint: IEndpoint): string {
        const fixedDrivesEncryptionDisplay = [];
        if ( endpoint.fixed_drives_encryption != null ) {
            endpoint.fixed_drives_encryption.forEach( encryptionType => {
                fixedDrivesEncryptionDisplay.push(getVolumeEncryptionTypeDisplay(encryptionType, this.translate));
            });
        }
        return fixedDrivesEncryptionDisplay.join(', ');
    }

   setSelectedTarget(target: string) {
        if (this.targets.indexOf(target) == -1) {
            target = this.targets[0];
        }
        this.selectedTarget = target;
        localStorage.setItem('endpoints_target', target);
        this.setTargetColumnIndex();
        this.handlePermissionDrivenAttrs()
    }

    private setTargetColumnIndex() {
        this.targets.forEach((value, index) => {
            if (value == this.selectedTarget) {
                this.targetColumnsIndex = index;
                this.targetSaveId = 'endpoints-' + this.selectedTarget;
            }
        });
        this.targetColumns = [];
        this.targetColumnsMetadata[this.targetColumnsIndex].forEach( targetColumn => {
            this.targetColumns.push({...targetColumn});
        });
    }


    hideColumn(column: string) {
        this.columnChooser?.hideColumn(column);
    }
    showColumn(columnName: string) {
        const columns: IColumnChooserElement[] = this.targetColumns;
        let show = false;
        columns.forEach(column => {
            if ( column.value == columnName && column.selected) {
                show = true;
            }
        });
        if ( show ) {
            this.columnChooser?.showColumn(columnName);
        } else {
            this.columnChooser?.hideColumn(columnName);
        }
    }

    getEncryptionTypeDisplay(os_encryption: any) {
        return getVolumeEncryptionTypeDisplay(os_encryption, this.translate);
    }

    getSecureDriveDisplay(isConfigured: number) {
        if (isConfigured) {
            return this.translate.instant('D3ED_CONFIGURED');
        }
        return '';
    }

    getVersionDisplayText(version: string) {
        return version === "0.0.0" ? this.translate.instant("N_A") : version;
    }

    getExportQuery() {
        const query = this.getQuery(false);
        query['include_group_info'] = true;

        let columns = [];
        this.targetColumns.forEach( column => {
            if (column.selected && !column.hidden) {

                columns.push({'attr': this.queryColumnNames[column.value], 'display': column.title});
            }
        });
        query['export_columns'] = columns;
        query['target'] = this.selectedTarget;
        query['translations'] = {};
        query['translations']['activeLock'] = {'standingGuard': this.fStandingGuard,
                                               'detected' : this.fThreatDetected };

        query['translations']['manualLock'] = {'locked': this.fLocked,
                                               'unlocked': this.fUnlocked,
                                               'pendingLock': this.fPendingLock,
                                               'pendingUnlock': this.fPendingUnlock };

        query['translations']['protectors'] = {};
        const protectorKeys = Object.keys(VolumeProtectorType).filter((v) => isNaN(Number(v)));
        protectorKeys.forEach( (key, index) => {
            const protection: IVolumeProtection = new class implements IVolumeProtection {
                key_id: string = '';
                protector_type: VolumeProtectorType = VolumeProtectorType[key];
            };
            const translationValue = getProtectorDisplayValue(protection, this.translate);
            query['translations']['protectors'][index.toString()] = translationValue.toString();
        });

        query['translations']['encryptionType'] = {};
        const encryptionTypeKeys = Object.keys(VolumeEncryptionType).filter((v) => isNaN(Number(v)));
        encryptionTypeKeys.forEach( (key, index) => {
            const encryptionType: VolumeEncryptionType = VolumeEncryptionType[key];
            const translationValue = getVolumeEncryptionTypeDisplay(encryptionType, this.translate);
            query['translations']['encryptionType'][index.toString()] = translationValue.toString();
        });

        return query;
    }
    exportCSV() {
        const target = this.selectedTarget;
        const filename: string = 'endpoints-' + target + '.csv';
        const query = this.getExportQuery();
        this.d3eService.get_endpoints_csv_token(query)
          .pipe(
              catchError(() => of({} as IEndpointExportToken)),
              untilDestroyed(this))
          .subscribe(response => {
                const url = this.configurationService.apiUrl + '/home/devices/export/csv?q=' + response.token;
                multiDownload([url], {
                  // tslint:disable-next-line:no-shadowed-variable
                  rename: (url, index, urls) => filename
                });
              });
    }

    exportJSON() {
        const target = this.selectedTarget;
        const filename: string = 'endpoints-' + target + '.json';
        const query = this.getExportQuery();
        this.d3eService.get_endpoints_csv_token(query)
          .pipe(
              catchError(() => of({} as IEndpointExportToken)),
              untilDestroyed(this))
          .subscribe(response => {
                const url = this.configurationService.apiUrl + '/home/devices/export/json?q=' + response.token;
                multiDownload([url], {
                  // tslint:disable-next-line:no-shadowed-variable
                  rename: (url, index, urls) => filename,
                });
              }
              );
    }

    clearBitlockerFilter() {
        this.selectedBitlockerCompliance = null;
        this.getEndpoints(true);
    }

    isTextOverflowing(e) {
        return e.scrollWidth <= e.clientWidth;
    }

    getFdeVolume(device: IEndpoint): IVolume | null {
        return (
            device.config?.disks
                ?.flatMap(disk => disk.volumes ?? [])
                .find(volume => volume.volume_encryption?.encryption === 'CigentFDE')
            ?? null
        );
    }

    getPbaInfo(device: IEndpoint): IPBAInfo | null {
        return (
            device.config?.disks?.find(disk => disk.pba_info?.installed)?.pba_info ??
            null
        );
    }

    getInstalledProducts(device: IEndpoint): string {
        const products: string[] = [];

        if (this.getPbaInfo(device)) {
            products.push('PBA');
        }

        if (this.getFdeVolume(device)) {
            products.push('FDE');
        }

        return products.join(', ');
    }

    getPbaSerial(device: IEndpoint): string | null {
        return (
            device.config?.disks?.find(disk => disk.pba_info?.installed)?.serial ??
            null
        );
    }

    getPbaLicenseInfo(device: IEndpoint, serial: string | null): IPbaLicenseInfo | null {
        if (!serial) {
            return null;
        }

        return (
            device.db_pba_license_info?.find(
                (license: IPbaLicenseInfo) => license.serial === serial
            ) ?? null
        );
    }

    getProductLicenseStatus(licenseInfo: IPbaLicenseInfo | IFdeLicenseInfo | null | undefined): string {
        if (!licenseInfo?.maintenance_end_dttm) {
            return 'Non-Licensed';
        }

        const expiration = new Date(licenseInfo.maintenance_end_dttm);

        if (isNaN(expiration.getTime())) {
            return 'Non-Licensed';
        }

        const now = new Date();

        if (expiration < now) {
            return 'Non-Licensed';
        }

        const threeMonthsFromNow = new Date();
        threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

        if (expiration <= threeMonthsFromNow) {
            return 'Ending Soon';
        }

        return 'Licensed';
    }

    getAggregateLicenseStatus(device: IEndpoint): string {
        const statuses: string[] = [];

        if (this.getPbaInfo(device)) {
            const pbaLicenseInfo = this.getPbaLicenseInfo(
                device,
                this.getPbaSerial(device)
            );

            statuses.push(this.getProductLicenseStatus(pbaLicenseInfo));
        }

        if (this.getFdeVolume(device)) {
            statuses.push(this.getProductLicenseStatus(device.db_fde_license_info));
        }

        if (statuses.length === 0) {
            return 'Non-Licensed';
        }

        if (statuses.includes('Non-Licensed')) {
            return 'Non-Licensed';
        }

        if (statuses.includes('Ending Soon')) {
            return 'Ending Soon';
        }

        return 'Licensed';
    }

    hasSingleSelectedEndpoint(): boolean {
        return this.endpoints.filter(e => e.checked).length === 1;
    }

    getSingleSelectedEndpoint(): IEndpoint | null {
        const selected = this.endpoints.filter(e => e.checked);

        return selected.length === 1
            ? selected[0].endpointData
            : null;
    }

    hasSingleSelectedPbaEndpoint(): boolean {
        const endpoint = this.getSingleSelectedEndpoint();

        return !!endpoint && !!this.getPbaInfo(endpoint);
    }

    hasSingleSelectedFdeEndpoint(): boolean {
        const endpoint = this.getSingleSelectedEndpoint();

        return !!endpoint && !!this.getFdeVolume(endpoint);
    }

    private getFdeDiskSerial(endpoint: IEndpoint): string | null {
        const disk = endpoint.config?.disks?.find(disk =>
            disk.volumes?.some(
                volume => volume.volume_encryption?.encryption === 'CigentFDE'
            )
        );

        return disk?.serial ?? null;
    }

    protected readonly UserRole = UserRole;
    protected readonly PolicyCompliance = PolicyCompliance;
}


